/**
 * Dropdown Component
 * Reusable dropdown with click-outside handling
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface DropdownOption {
  value: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

export interface DropdownProps<T extends string = string> {
  options: DropdownOption[];
  value: T;
  onChange: (value: T) => void;
  triggerIcon?: React.ComponentType<{ className?: string }>;
  triggerLabel?: string;
  ariaLabel?: string;
  className?: string;
}

export function Dropdown<T extends string = string>({
  options,
  value,
  onChange,
  triggerIcon: TriggerIcon,
  triggerLabel,
  ariaLabel = 'Select option',
  className = '',
}: DropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentOption = options.find((opt) => opt.value === value) || options[0];
  const CurrentIcon = currentOption.icon;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-2 sm:px-3 py-1.5 text-sm rounded-lg transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 flex items-center gap-2 min-w-0 sm:min-w-[120px] justify-between"
        aria-label={ariaLabel}
      >
        <span className="flex items-center gap-2 min-w-0">
          {TriggerIcon && <TriggerIcon className="w-4 h-4 flex-shrink-0" />}
          {!TriggerIcon && CurrentIcon && <CurrentIcon className="w-4 h-4 flex-shrink-0" />}
          <span className="hidden sm:inline">{triggerLabel || currentOption.label}</span>
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 sm:w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
          {options.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value as T);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-sm text-left flex items-center gap-2 transition-colors ${
                  value === option.value
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {Icon && <Icon className="w-4 h-4" />}
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
